import React from 'react';
import { CandidateOpportunitiesConst, OpportunitiesConst } from '@axiom/const';
import type {
  Candidate,
  Opportunity,
  OpportunityPosition,
} from '@axiom/validation';

import { PositionUtil } from '../../utils/position-util';

const { CandidateStatuses } = CandidateOpportunitiesConst;

const getPositionCopy = ({
  position,
  index,
  opportunity,
  isAnotherCandidateSelected,
  actionVerb,
}: {
  position: OpportunityPosition;
  index: number;
  opportunity: Opportunity;
  isAnotherCandidateSelected: boolean;
  actionVerb: string;
}): React.JSX.Element | string => {
  if (opportunity.selfService && !opportunity.isSynced) {
    return <div>Opp not synced to SF</div>;
  }

  if (isAnotherCandidateSelected) {
    return `Talent selected, can't change to ${PositionUtil.getNameLabel({
      position,
      ordinal: index + 1,
    })}`;
  }
  return (
    <div className="max-lines-1">
      {actionVerb}{' '}
      {PositionUtil.getNameLabel({
        position,
        ordinal: index + 1,
      })}
    </div>
  );
};

export const OpportunityTalentMenuUtil = {
  isAnotherCandidateSelected: ({
    position,
    candidateId,
  }: {
    position?: OpportunityPosition;
    candidateId: Candidate['id'];
  }): boolean => {
    return !!position?.candidates.some(
      ({ candidateStatus, candidateId: positionCandidateId }) =>
        candidateId !== positionCandidateId &&
        candidateStatus === CandidateStatuses.Selected
    );
  },

  getCannotChangeStatusCopy: ({
    isAnotherCandidateSelected,
    opportunity,
  }: {
    isAnotherCandidateSelected: boolean;
    opportunity: Opportunity;
  }): string => {
    if (isAnotherCandidateSelected && opportunity.isFulfillmentActive) {
      return "Talent selected, can't change status";
    }

    if (!opportunity.isFulfillmentActive) {
      return opportunity.stage === OpportunitiesConst.Stages.ClosedWon ||
        opportunity.stage === OpportunitiesConst.Stages.ClosedLost
        ? `${opportunity.stage}, can't change status`
        : `Fulfilled, can't change status`;
    }

    return "Can't change status";
  },

  getMovePositionCopy: ({
    position,
    index,
    opportunity,
    isAnotherCandidateSelected,
  }: {
    position: OpportunityPosition;
    index: number;
    opportunity: Opportunity;
    isAnotherCandidateSelected: boolean;
  }): React.JSX.Element | string => {
    return getPositionCopy({
      position,
      index,
      opportunity,
      isAnotherCandidateSelected,
      actionVerb: 'Move to',
    });
  },

  getAddPositionCopy: ({
    position,
    index,
    opportunity,
    isAnotherCandidateSelected,
  }: {
    position: OpportunityPosition;
    index: number;
    opportunity: Opportunity;
    isAnotherCandidateSelected: boolean;
  }): React.JSX.Element | string => {
    return getPositionCopy({
      position,
      index,
      opportunity,
      isAnotherCandidateSelected,
      actionVerb: 'Add to',
    });
  },
};
